import React from 'react';
import {
  Shield,
  Users,
  Heart,
  Target,
  Lightbulb,
  Home,
  Building,
  Handshake,
  Phone,
  Play,
  ArrowRight,
  CheckCircle,
  Wrench,
} from 'lucide-react';
import MainHeader from '../../Components/Header/MainHeader';
import MainFooter from '../../Components/Footer/MainFooter';

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.rekebisha.RekebishaClient&pcampaignid=web_share";

export default function AboutUsPage() {
  const values = [
    {
      icon: Target,
      title: "Our Mission",
      description: "To remove the middlemen from housing and home services in Kenya by connecting tenants, buyers, homeowners, agents and fundis directly on one trusted app.",
    },
    {
      icon: Shield,
      title: "Verified Both Ways",
      description: "Every agent, homeowner and fundi on Rekebisha is verified, so you can browse, enquire and hire with confidence.",
    },
    {
      icon: Handshake,
      title: "Direct Connection",
      description: "No agent fees, no waiting on hold. You talk to the person who owns the property or who will do the work.",
    },
    {
      icon: Lightbulb,
      title: "Simple Technology",
      description: "A single app for searching, listing, hiring and communicating, built for how people actually find a home and keep it running.",
    },
  ];

  const stats = [
    { number: "1,000+", label: "Properties Listed", icon: Home },
    { number: "2,000+", label: "Verified Fundis", icon: Wrench },
    { number: "47", label: "Counties Covered", icon: Building },
    { number: "10,000+", label: "Happy Clients", icon: Heart },
  ];

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <MainHeader />

      {/* Hero Section */}
      <section
        className="relative bg-cover bg-center"
        style={{ backgroundImage: `url(https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=1600&auto=format&fit=crop)` }}
      >
        <div className="absolute inset-0 bg-navy/75"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            About Rekebisha
          </h1>
          <p className="text-lg text-gray-200 max-w-2xl mx-auto">
            Rekebisha is Kenya's marketplace for homes and fundis. We help people find a house to let or buy and hire the skilled hands to keep it running, all without a middleman standing in between.
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => (
              <div key={index}>
                <stat.icon className="w-8 h-8 text-brandgreen mx-auto mb-3" />
                <div className="text-4xl font-bold text-navy mb-1">{stat.number}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 bg-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-white mb-6">Our Story</h2>
              <div className="space-y-4 text-gray-200 text-lg leading-relaxed">
                <p>
                  Rekebisha started as a way to make home repairs easier to find in Kenya. Thousands of plumbers,
                  electricians, carpenters and painters built portfolios on our platform, and clients finally had a
                  way to see a fundi's work and rating before picking up the phone.
                </p>
                <p>
                  As we listened to those same users, one problem kept coming up louder than the rest: finding a
                  home to let or buy, without paying layers of agent fees, was just as hard. So we built the other
                  half of the marketplace.
                </p>
                <p>
                  Today Rekebisha is where people go to find apartments, bungalows, mansionettes, commercial spaces
                  and luxury homes across Kenya, and to hire the verified fundi who will fix, fit or finish whatever
                  the place needs. Both sides, one app, no middlemen.
                </p>
              </div>
            </div>

            <div className="relative rounded-2xl overflow-hidden h-96">
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(https://images.unsplash.com/photo-1523217582562-09d0def993a6?q=80&w=900&auto=format&fit=crop)` }}
              ></div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-navy mb-4">What Drives Us</h2>
            <p className="text-xl text-gray-600">The principles behind every listing and every fundi on Rekebisha</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8">
                <div className="w-14 h-14 bg-brandgreen/10 rounded-full flex items-center justify-center mb-6">
                  <value.icon className="w-7 h-7 text-brandgreen" />
                </div>
                <h3 className="text-xl font-bold text-navy mb-3">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Serve Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-navy mb-4">Who Rekebisha Is For</h2>
            <p className="text-xl text-gray-600">One app, built for every side of the marketplace</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="w-16 h-16 bg-navy rounded-full flex items-center justify-center mb-6">
                <Home className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-navy mb-4">Buyers And Tenants</h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-brandgreen mr-3 mt-0.5 flex-shrink-0" />Search verified homes to let or buy</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-brandgreen mr-3 mt-0.5 flex-shrink-0" />Message or call the owner directly</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-brandgreen mr-3 mt-0.5 flex-shrink-0" />Avoid agent fees and long delays</li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="w-16 h-16 bg-brandgreen rounded-full flex items-center justify-center mb-6">
                <Building className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-navy mb-4">Agents And Homeowners</h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-brandgreen mr-3 mt-0.5 flex-shrink-0" />List a property in minutes</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-brandgreen mr-3 mt-0.5 flex-shrink-0" />Reach genuine buyers and tenants</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-brandgreen mr-3 mt-0.5 flex-shrink-0" />Manage enquiries straight from the app</li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="w-16 h-16 bg-navy rounded-full flex items-center justify-center mb-6">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-navy mb-4">Clients Needing Repairs</h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-brandgreen mr-3 mt-0.5 flex-shrink-0" />Filter fundis by trade and location</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-brandgreen mr-3 mt-0.5 flex-shrink-0" />See past work and client ratings</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-brandgreen mr-3 mt-0.5 flex-shrink-0" />Call the fundi yourself, same day</li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="w-16 h-16 bg-brandgreen rounded-full flex items-center justify-center mb-6">
                <Wrench className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-navy mb-4">Fundis And Handymen</h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-brandgreen mr-3 mt-0.5 flex-shrink-0" />Build a portfolio of your best work</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-brandgreen mr-3 mt-0.5 flex-shrink-0" />Earn ratings that win you more jobs</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-brandgreen mr-3 mt-0.5 flex-shrink-0" />Get hired by clients in your county</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-4xl font-bold text-navy mb-6">Join Rekebisha Today</h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Whether you are searching for a home, listing one, or looking for a fundi to get the job done, Rekebisha connects you directly with the right people.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center px-8 py-4 bg-brandgreen text-white font-bold rounded-lg hover:bg-brandgreen-dark transition-colors duration-300"
              >
                <Play className="w-5 h-5 mr-2" />
                Download The App
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>

              <a
                href="tel:0733212124"
                className="text-xl font-bold text-navy hover:text-brandgreen transition-colors duration-300 flex items-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                0733 212 124
              </a>
            </div>
          </div>
        </div>
      </section>

      <MainFooter />
    </div>
  );
}
